import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getCurrentUser } from "../utils/auth";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";

const formatTimestamp = (value) => {
  if (!value) return "";
  const iso = /\d{4}-\d{2}-\d{2}T/.test(value) ? value : `${String(value).replace(/\s+/, "T")}Z`;
  const dt = new Date(iso);
  return Number.isNaN(dt.getTime()) ? value : dt.toLocaleString();
};

export default function AdminApiLogs() {
  const me = getCurrentUser();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionFilter, setActionFilter] = useState("ils_update_payload");
  const [limit, setLimit] = useState(200);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role;
      const search = new URLSearchParams();
      if (actionFilter) search.set("action", actionFilter);
      if (limit) search.set("limit", String(limit));
      const resp = await fetch(`${API_BASE}/admin/api-logs?${search.toString()}`, { headers });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.ok !== true) {
        throw new Error(data?.error || "Fetch failed");
      }
      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to load API logs.");
    } finally {
      setLoading(false);
    }
  }, [me?.email, me?.role, actionFilter, limit]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const displayRows = useMemo(() => rows.slice(), [rows]);

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold">API Logs</h1>
          <p className="text-sm text-slate-600">
            最近的關鍵 API 請求紀錄（含 iLS Update payload 要點），僅記錄關鍵欄位，不含 token。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            placeholder="action (e.g., ils_update_payload)"
            className="w-52 rounded border border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {[50, 100, 200, 500, 1000].map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={loadLogs}
            disabled={loading}
            className="rounded-md border px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-auto border rounded-md">
        <table className="min-w-full border-collapse text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">When</th>
              <th className="px-3 py-2 text-left font-semibold">Action</th>
              <th className="px-3 py-2 text-left font-semibold">Outbound</th>
              <th className="px-3 py-2 text-left font-semibold">Cust</th>
              <th className="px-3 py-2 text-left font-semibold">PO</th>
              <th className="px-3 py-2 text-left font-semibold">Load</th>
              <th className="px-3 py-2 text-left font-semibold">customerOrder</th>
              <th className="px-3 py-2 text-left font-semibold">requiredShipDate</th>
              <th className="px-3 py-2 text-left font-semibold">orderNo3rdParty</th>
              <th className="px-3 py-2 text-left font-semibold">routingRowId</th>
            </tr>
          </thead>
          <tbody>
            {loading && displayRows.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-slate-500">
                  Loading logs...
                </td>
              </tr>
            ) : displayRows.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-slate-500">
                  No logs captured yet.
                </td>
              </tr>
            ) : (
              displayRows.map((row, idx) => {
                const key = row.action ? `${row.action}-${row.created_at || idx}-${idx}` : `api-${idx}`;
                let payload = {};
                try {
                  payload = row.payload ? JSON.parse(row.payload) : {};
                } catch (_) {
                  payload = {};
                }
                return (
                  <tr key={key}>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {formatTimestamp(row.created_at)}
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">{row.action}</td>
                    <td className="border-t px-3 py-2 align-top">{payload.outbound || "-"}</td>
                    <td className="border-t px-3 py-2 align-top">{payload.custCode || "-"}</td>
                    <td className="border-t px-3 py-2 align-top">{payload.poNumber || "-"}</td>
                    <td className="border-t px-3 py-2 align-top">{payload.loadId || "-"}</td>
                    <td className="border-t px-3 py-2 align-top">{payload.customerOrder || "-"}</td>
                    <td className="border-t px-3 py-2 align-top">{payload.requiredShipDate || "-"}</td>
                    <td className="border-t px-3 py-2 align-top">{payload.orderNo3rdParty || "-"}</td>
                    <td className="border-t px-3 py-2 align-top">{payload.routingRowId ?? "-"}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
